import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { apiService } from '../services/api';

const DebugPage = () => {
  const { user } = useAuth();
  const { tenantName } = useTenant();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});

  // Load debug flags
  const loadDebugFlags = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDebugFlags();
      setFlags(response.flags || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load debug flags:', err);
      setError('Failed to load debug flags');
    } finally {
      setLoading(false);
    }
  };

  // Toggle debug flag
  const toggleFlag = async (flagName, currentValue) => {
    try {
      setUpdating(prev => ({ ...prev, [flagName]: true }));
      
      await apiService.updateDebugFlag(flagName, !currentValue);
      
      // Update local state
      setFlags(prev => prev.map(flag => 
        flag.flag_name === flagName 
          ? { ...flag, is_enabled: !currentValue }
          : flag
      ));
      
      console.log(`🐛 Debug flag ${flagName} ${!currentValue ? 'enabled' : 'disabled'}`);
      
    } catch (err) {
      console.error('Failed to update debug flag:', err);
      setError(`Failed to update ${flagName}`);
    } finally {
      setUpdating(prev => ({ ...prev, [flagName]: false }));
    }
  };

  // Reset all flags
  const resetAllFlags = async () => {
    try {
      setLoading(true);
      await apiService.resetDebugFlags();
      await loadDebugFlags(); // Reload flags
      console.log('🐛 All debug flags reset');
    } catch (err) {
      console.error('Failed to reset flags:', err);
      setError('Failed to reset debug flags');
    }
  };

  useEffect(() => {
    if (user && user.role === 'SUPERUSER') {
      loadDebugFlags();
    }
  }, [user]);

  // Check access - only SUPERUSER allowed
  if (!user || user.role !== 'SUPERUSER') {
    return (
      <div style={{
        backgroundColor: '#f8d7da',
        color: '#721c24',
        padding: '2rem',
        borderRadius: '0.5rem',
        margin: '1rem',
        textAlign: 'center'
      }}>
        <h2>🚫 Access Denied</h2>
        <p>Debug functionality is restricted to SUPERUSER role only.</p>
        <p>Current role: <strong>{user?.role || 'Unknown'}</strong></p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        fontSize: '1.2rem'
      }}>
        🔄 Loading debug flags...
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid #dc3545'
      }}>
        <div>
          <h1 style={{ 
            margin: 0, 
            color: '#dc3545',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🐛 Debug Control Panel
          </h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
            SUPERUSER Debug Tools - {tenantName}
          </p>
        </div>
        <button
          onClick={resetAllFlags}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          🔄 Reset All Flags
        </button>
      </div>

      {/* Warning Banner */}
      <div style={{
        backgroundColor: '#fff3cd',
        color: '#856404',
        padding: '1rem',
        borderRadius: '0.5rem',
        marginBottom: '2rem',
        border: '1px solid #ffeaa7'
      }}>
        <h3 style={{ margin: '0 0 0.5rem 0' }}>⚠️ Warning</h3>
        <p style={{ margin: 0 }}>
          This panel controls bug simulation for testing purposes. 
          Enabling flags will intentionally break functionality in this tenant only.
          Use with caution and reset flags after testing.
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {/* Debug Flags */}
      <div style={{
        display: 'grid',
        gap: '1rem'
      }}>
        {flags.map(flag => (
          <DebugFlagCard
            key={flag.flag_name}
            flag={flag}
            onToggle={toggleFlag}
            updating={updating[flag.flag_name] || false}
          />
        ))}
      </div>

      {flags.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#666'
        }}>
          No debug flags found for this tenant.
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: '2rem',
        paddingTop: '1rem',
        borderTop: '1px solid #dee2e6',
        fontSize: '0.9rem',
        color: '#666',
        textAlign: 'center'
      }}>
        Debug flags are tenant-scoped and cached for 5 minutes.
        Changes affect only the current tenant: <strong>{tenantName}</strong>
      </div>
      </div>
    </div>
  );
};

// Debug Flag Card Component
const DebugFlagCard = ({ flag, onToggle, updating }) => {
  const getStatusStyle = (enabled) => ({
    padding: '0.25rem 0.75rem',
    borderRadius: '1rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    backgroundColor: enabled ? '#28a745' : '#6c757d',
    color: 'white'
  });

  const getSeverityColor = (flagName) => {
    if (flagName.includes('pagination') || flagName.includes('search')) return '#fd7e14'; // Orange
    if (flagName.includes('fee') || flagName.includes('balance')) return '#dc3545'; // Red
    if (flagName.includes('notification') || flagName.includes('delay')) return '#ffc107'; // Yellow
    return '#6f42c1'; // Purple
  };

  return (
    <div style={{
      border: '1px solid #dee2e6',
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backgroundColor: flag.is_enabled ? '#f8f9fa' : 'white',
      borderLeft: `4px solid ${getSeverityColor(flag.flag_name)}`
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{
            margin: '0 0 0.5rem 0',
            color: '#333',
            fontSize: '1.1rem'
          }}>
            {flag.flag_description}
          </h3>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '0.9rem',
            fontFamily: 'monospace'
          }}>
            Flag: <code>{flag.flag_name}</code>
          </p>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span style={getStatusStyle(flag.is_enabled)}>
            {flag.is_enabled ? '🔴 Active' : '✅ Disabled'}
          </span>
          
          <button
            onClick={() => onToggle(flag.flag_name, flag.is_enabled)}
            disabled={updating}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: flag.is_enabled ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: updating ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              opacity: updating ? 0.6 : 1
            }}
          >
            {updating ? '...' : (flag.is_enabled ? 'Disable' : 'Enable')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebugPage; 